import { Link } from 'react-router-dom';

import { Icon, SeamStep } from '@/components/ui';
import { SEAM, SITE } from '@/constants';
import { FOOTER_NAV } from '@/data';

import { Logo } from '../Logo';

import styles from './Footer.module.css';

/**
 * The end of the migration: the record is down to a sliver at the left edge,
 * and the thread fades out into it rather than stopping.
 *
 * It also carries `id="contact"`, and with it the e-mail, phone and address
 * that used to have a band of their own. With the form parked that band was a
 * footer in disguise, sitting directly under a call to action that said the
 * same thing — so the page closes once now, here.
 *
 * The footer draws its own `SeamStep` because it is a layout component: the
 * page above it ends at the pilot, and nothing else would carry the line
 * across the join.
 */
export function Footer() {
  return (
    <>
      {/* `SEAM.retailers` while section 08 is parked; back to `SEAM.pilot`
          when it returns — see src/pages/HomePage/HomePage.tsx. */}
      <SeamStep from={SEAM.retailers} to={SEAM.footer} tone="darkest" />

      <footer id="contact" className={styles.footer}>
        <div className={styles.scar} aria-hidden="true" />
        <span className={styles.thread} aria-hidden="true" />

        <div className={styles.body}>
          <div className={styles.top}>
            <div className={styles.brand}>
              <Logo inverted />
              <p className={styles.tagline}>{SITE.description}</p>
              <p className={styles.tagline}>
                Built by {SITE.company} in {SITE.address.city}.
              </p>
            </div>

            {FOOTER_NAV.map((group) => (
              <nav key={group.title} aria-label={group.title}>
                <h2 className={styles.groupTitle}>{group.title}</h2>
                <ul className={styles.groupList} role="list">
                  {group.items.map((item) => (
                    <li key={item.label}>
                      <Link className={styles.groupLink} to={item.to}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}

            <div>
              <h2 className={styles.groupTitle}>Contact</h2>
              <ul className={styles.contactList} role="list">
                <li className={styles.contact}>
                  <Icon name="mail" size={16} className={styles.contactIcon} />
                  <a className={styles.groupLink} href={`mailto:${SITE.email}`}>
                    {SITE.email}
                  </a>
                </li>
                <li className={styles.contact}>
                  <Icon name="phone" size={16} className={styles.contactIcon} />
                  <a className={styles.groupLink} href={`tel:${SITE.phone.replace(/\s/g, '')}`}>
                    {SITE.phone}
                  </a>
                </li>
                <li className={styles.contact}>
                  <Icon name="map-pin" size={16} className={styles.contactIcon} />
                  <address className={styles.address}>
                    {SITE.address.line1}
                    <br />
                    {SITE.address.line2}
                    <br />
                    {SITE.address.city}, {SITE.address.country}
                  </address>
                </li>
              </ul>
            </div>
          </div>

          <div className={styles.bottom}>
            <p>
              Copyright &copy; {new Date().getFullYear()} {SITE.company}. All rights reserved.
            </p>
            {/*
              Parked. To restore: uncomment this list and add `ROUTES` back to
              the '@/constants' import above. The /privacy and /terms routes are
              still live — they simply have nothing linking to them meanwhile.
            */}
            {/*
            <ul className={styles.legalLinks} role="list">
              <li>
                <Link className={styles.groupLink} to={ROUTES.privacy}>
                  Privacy
                </Link>
              </li>
              <li>
                <Link className={styles.groupLink} to={ROUTES.terms}>
                  Terms
                </Link>
              </li>
            </ul>
            */}
          </div>

          {/* The wordmark, outlined and cropped by the page edge. */}
          <span className={styles.mark} aria-hidden="true">
            {SITE.name}
          </span>
        </div>
      </footer>
    </>
  );
}
